import { supabase } from '../lib/supabase';

/**
 * Get pricing tiers from the database
 */
export const getPricingTiers = async () => {
  try {
    const { data, error } = await supabase
      .from('pricing_tiers')
      .select('*')
      .order('min_quantity');

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching pricing tiers:', error);
    return [];
  }
};

/**
 * Get shipping settings from the database
 */
export const getShippingSettings = async () => {
  try {
    const { data, error } = await supabase
      .from('shipping_settings')
      .select('*')
      .limit(1);

    if (error) {
      throw error;
    }
    
    // Return the first row if it exists, otherwise return defaults
    return data && data.length > 0 ? data[0] : { standard_shipping_cost: 0, free_shipping_threshold: 0 };
  } catch (error) {
    console.error('Error fetching shipping settings:', error);
    return { standard_shipping_cost: 0, free_shipping_threshold: 0 };
  }
};

/**
 * Calculate the price per unit for a given quantity using pricing tiers
 */
export const calculatePricePerUnit = (quantity, pricingTiers) => {
  if (!pricingTiers || pricingTiers.length === 0) {
    return 0;
  }

  // Find the appropriate pricing tier for the quantity
  const tier = pricingTiers.find(tier => 
    quantity >= tier.min_quantity && quantity <= tier.max_quantity
  );

  return tier ? tier.price_per_unit : 0;
};

/**
 * Calculate the total price for a cart item using standard pricing
 */
export const calculateItemPrice = async (product, quantity) => {
  try {
    // If product doesn't use standard pricing, return the fixed price
    if (!product.use_standard_pricing) {
      return product.price * quantity;
    }

    // Get pricing tiers
    const pricingTiers = await getPricingTiers();
    
    // Calculate price per unit based on quantity
    const pricePerUnit = calculatePricePerUnit(quantity, pricingTiers);
    
    // If no tier found, fall back to product price
    if (pricePerUnit === 0) {
      return product.price * quantity;
    }

    return pricePerUnit * quantity;
  } catch (error) {
    console.error('Error calculating item price:', error);
    // Fall back to product price
    return product.price * quantity;
  }
};

/**
 * Calculate cart total with quantity-based discounts applied as separate items
 */
export const calculateCartTotalWithDiscounts = async (cartItems) => {
  try {
    console.log('=== CALCULATING CART TOTALS WITH DISCOUNTS ===');
    console.log('Cart items:', cartItems);
    
    // Separate items by pricing type
    const standardPricingItems = cartItems.filter(item => item.use_standard_pricing);
    const fixedPricingItems = cartItems.filter(item => !item.use_standard_pricing);
    
    console.log('Standard pricing items:', standardPricingItems);
    console.log('Fixed pricing items:', fixedPricingItems);
    
    // Calculate total quantity for standard pricing items
    const totalStandardQuantity = standardPricingItems.reduce((sum, item) => sum + item.quantity, 0);
    console.log('Total standard quantity:', totalStandardQuantity);
    
    // Get pricing tiers
    const pricingTiers = await getPricingTiers();
    console.log('Pricing tiers:', pricingTiers);
    
    // Calculate original total for standard pricing items
    const originalStandardTotal = standardPricingItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    
    // Calculate discounted total for standard pricing items
    let discountedStandardTotal = 0;
    let discountItems = [];
    
    if (totalStandardQuantity > 0 && pricingTiers.length > 0) {
      // Find the appropriate pricing tier
      const tier = pricingTiers.find(t => 
        totalStandardQuantity >= t.min_quantity && totalStandardQuantity <= t.max_quantity
      );
      
      if (tier) {
        // Check if any standard pricing item would benefit from this tier
        const hasDiscount = standardPricingItems.some(item => tier.price_per_unit < item.price);
        
        if (hasDiscount) {
          // Apply discount to all standard pricing items
          discountedStandardTotal = totalStandardQuantity * tier.price_per_unit;
          const discountAmount = originalStandardTotal - discountedStandardTotal;
          
          if (discountAmount > 0) {
            // Create discount item
            const discountLabel = getDiscountLabel(tier, pricingTiers);
            
            const discountItem = {
              id: `discount-${tier.min_quantity}-${tier.max_quantity}`,
              name: discountLabel,
              price: -discountAmount,
              amount: discountAmount, // Add amount property for display
              quantity: 1,
              isDiscount: true,
              tier: tier
            };
            
            console.log('Creating discount item:', discountItem);
            discountItems.push(discountItem);
          }
        } else {
          discountedStandardTotal = originalStandardTotal;
        }
      } else {
        discountedStandardTotal = originalStandardTotal;
      }
    } else {
      discountedStandardTotal = originalStandardTotal;
    }
    
    // Calculate fixed pricing items total
    const fixedPricingTotal = fixedPricingItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    
    // Calculate subtotal
    const subtotal = discountedStandardTotal + fixedPricingTotal;
    
    // Calculate shipping
    const shipping = await calculateShippingCost(subtotal);
    
    // Calculate total
    const total = subtotal + shipping;
    
    const result = {
      subtotal,
      shipping,
      total,
      discountItems,
      originalStandardTotal,
      discountedStandardTotal,
      fixedPricingTotal
    };
    
    console.log('Final cart totals result:', result);
    return result;
  } catch (error) {
    console.error('Error calculating cart total with discounts:', error);
    // Fallback to simple calculation
    const subtotal = cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const shipping = await calculateShippingCost(subtotal);
    return {
      subtotal,
      shipping,
      total: subtotal + shipping,
      discountItems: [],
      originalStandardTotal: 0,
      discountedStandardTotal: 0,
      fixedPricingTotal: subtotal
    };
  }
};

/**
 * Get discount label based on tier
 */
const getDiscountLabel = (tier, allTiers) => {
  return `Korting vanaf ${tier.min_quantity} kaarten`;
};

/**
 * Calculate pricing details for display (original price vs discounted price)
 */
export const calculateItemPricingDetails = async (product, quantity) => {
  try {
    // If product doesn't use standard pricing, return same price for both
    if (!product.use_standard_pricing) {
      return {
        originalPrice: product.price,
        discountedPrice: product.price,
        hasDiscount: false,
        discountAmount: 0
      };
    }

    // Get pricing tiers
    const pricingTiers = await getPricingTiers();
    
    // Calculate price per unit based on quantity
    const pricePerUnit = calculatePricePerUnit(quantity, pricingTiers);
    
    // If no tier found, fall back to product price
    if (pricePerUnit === 0) {
      return {
        originalPrice: product.price,
        discountedPrice: product.price,
        hasDiscount: false,
        discountAmount: 0
      };
    }

    const hasDiscount = pricePerUnit < product.price;
    const discountAmount = product.price - pricePerUnit;

    return {
      originalPrice: product.price,
      discountedPrice: pricePerUnit,
      hasDiscount,
      discountAmount
    };
  } catch (error) {
    console.error('Error calculating item pricing details:', error);
    // Fall back to product price
    return {
      originalPrice: product.price,
      discountedPrice: product.price,
      hasDiscount: false,
      discountAmount: 0
    };
  }
};

/**
 * Calculate shipping cost based on order total and shipping settings
 */
export const calculateShippingCost = async (orderTotal) => {
  try {
    const shippingSettings = await getShippingSettings();
    
    // If order total meets free shipping threshold, return 0
    if (shippingSettings.free_shipping_threshold > 0 && 
        orderTotal >= shippingSettings.free_shipping_threshold) {
      return 0;
    }
    
    return shippingSettings.standard_shipping_cost || 0;
  } catch (error) {
    console.error('Error calculating shipping cost:', error);
    return 0;
  }
};

/**
 * Calculate total cart price with standard pricing and shipping
 */
export const calculateCartTotal = async (cartItems) => {
  try {
    let subtotal = 0;
    
    // Calculate subtotal for each item
    for (const item of cartItems) {
      const itemTotal = await calculateItemPrice(item, item.quantity);
      subtotal += itemTotal;
    }
    
    // Calculate shipping
    const shipping = await calculateShippingCost(subtotal);
    
    return {
      subtotal: subtotal,
      shipping: shipping,
      total: subtotal + shipping
    };
  } catch (error) {
    console.error('Error calculating cart total:', error);
    // Fall back to simple calculation
    const subtotal = cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);
    return {
      subtotal: subtotal,
      shipping: 0,
      total: subtotal
    };
  }
};